import { Plus, Minus } from 'lucide-react';
import { ExamplesOfQuestionsSlider } from '@/components/examples-of-questions/examples-of-question';
import { useCssVariable } from '@/hooks/use-css-variable';
import { fetcher } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { QuestionAnswer } from '@shared/types/schema';
import { GameCarousel } from '@/components/games/game-carousel';
import { useQueryParam } from '@/hooks/use-query-param';
import { HeroSection } from './hero-section';
import HeroSlider from '@/components/HeroSlider';
import { CategoriesGames } from '@/types/category';

export function About() {
  const utmSource = useQueryParam('utm_source');

  useCssVariable('--main-container-max-width', '100%', { removeOnUnmount: true });

  const { data: categoriesGames = [] } = useQuery<CategoriesGames[]>({
    queryKey: ['api/games/categories'],
    queryFn: fetcher,
  });

  const { data: questionsAnswers = [] } = useQuery<QuestionAnswer[]>({
    queryKey: ['/api/questions-answers'],
    queryFn: fetcher,
  });

  const toggleQuestion = (id: string) => {
    let question = document.getElementById(id);
    let answer = question?.getElementsByClassName('answer')[0] as HTMLElement;
    if (!question || !answer) {
      return;
    }

    let plus = question?.getElementsByClassName('plus')[0] as HTMLElement;
    let minus = question?.getElementsByClassName('minus')[0] as HTMLElement;

    if (answer.style.opacity === '0') {
      answer.style.height = '100%';
      answer.style.opacity = '1';
      answer.classList.add('pb-8');
      plus.style.display = 'none';
      minus.style.display = 'block';
    } else {
      answer.style.height = '0';
      answer.style.opacity = '0';
      answer.classList.remove('pb-8');
      plus.style.display = 'block';
      minus.style.display = 'none';
    }
  };

  const scrollToGames = () => {
    const gamesSection = document.getElementById('hero-slider');
    if (gamesSection) {
      gamesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="main-container">
      <HeroSection source={utmSource} onChooseGame={scrollToGames} />
      <section id="hero-slider" className="py-6">
        <HeroSlider />
      </section>

      <section id="games" className="flex flex-col gap-8 md:gap-14 mt-8 md:mt-14">
        {categoriesGames.map((cat) => (
          <GameCarousel
            key={`cat-${cat.id}`}
            categoryId={cat.id}
            title={cat.name}
            games={cat.games}
          />
        ))}
      </section>

      <section>
        <h2 className="text-center">Примеры вопросов</h2>
        <ExamplesOfQuestionsSlider />
      </section>

      {!!questionsAnswers.length && (
        <section>
          <h2 className="text-center">Часто спрашивают</h2>
          <div className="flex flex-col items-center gap-2">
            {questionsAnswers.map((question, index) => (
              <div
                className="max-w-[890px] w-full bg-purple-800/10 px-8 rounded-md cursor-pointer"
                onClick={() => toggleQuestion(`questions_answers_${question.id}`)}
                key={`questions_answers_${question.id}`}
                id={`questions_answers_${question.id}`}
              >
                <h3 className="text-3xl font-medium py-6 flex items-center justify-between">
                  {question.question}
                  <div className="p-1 bg-primary inline-block rounded-full ">
                    <Plus className="plus [&_svg]:fill-black" />
                    <Minus className="minus [&_svg]:fill-black hidden" />
                  </div>
                </h3>
                <p
                  id={`answer_${index}`}
                  className="answer font-medium text-2xl transition-all duration-700 overflow-hidden "
                  style={{ height: 0, opacity: 0 }}
                >
                  {question.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
